import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Company } from '../../../models/companies/company';
import { OperationResult } from '../../../models/server-result/operation-result';
import { User } from '../../../models/User/user';
import { ServerApiService } from '../../../server-api/server-api.service';
import { AppContextService } from '../../../services/app-context.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { LogService } from '../../../services/log/log.service';
import { finalize, forkJoin } from 'rxjs';
import { OperationError } from '../../../models/server-result/operation-error';
import { CodeDecodeValue } from '../../../models/common/code-decode-value';
import { UtilitiesService } from '../../../services/utils/utilities.service';
import { TimeZoneValue } from '../../../models/common/time-zone-value';
import { ServerErrors } from '../../../models/enums/server-errors';
import { TranslateService } from '../../../services/translate/translate.service';
import { CompanyApiService } from '../../../server-api/company-api/company-api.service';
import { RouteAuthorizationService } from '../../../services/authorization/route-authorization.service';
import { BreadcrumbItem } from '../../../shared-components/custom-breadcrumbs/custom-breadcrumbs.component';
import { ComponentType, FormValidationUtils } from '../../../utils/form-validation';
import { SmartFormBaseComponent } from '../../../utils/smart-form-base.component';
import { CustomInputComponent } from '../../../shared-components/custom-input/custom-input.component';
import { CustomSelectComponent, SelectOption } from '../../../shared-components/custom-select/custom-select.component';

@Component({
	selector: 'eif-edit-company',
	templateUrl: './edit-company.component.html',
	styleUrls: ['./edit-company.component.scss'],
	standalone: false,
})
export class EditCompanyComponent extends SmartFormBaseComponent implements OnInit, AfterViewInit {
	public company: Company;
	public loggedUser: User | null = new User();
	public isPerformingRequest = false;
	public languageCodes: CodeDecodeValue[];
	public timeZones: TimeZoneValue[];

	public breadcrumbItems: BreadcrumbItem[] = [];

	public languageOptions: SelectOption[] = [];
	public timeZoneOptions: SelectOption[] = [];

	private get inputRules() {
		return CustomInputComponent.createRules(this.translateService);
	}

	private get selectRules() {
		return CustomSelectComponent.createRules(this.translateService);
	}

	private get activityCodeValidationRules() {
		return [
			this.inputRules.required(),
			this.inputRules.custom(
				(value) => {
					if (!value || value === '') return true;
					const numValue = Number(value);
					return !isNaN(numValue) && Number.isInteger(numValue) && numValue >= 0;
				},
				'Activity code must be a valid integer',
				'activityCode'
			),
		];
	}

	private get shareCapitalValidationRules() {
		return [
			this.inputRules.required(),
			this.inputRules.custom(
				(value) => {
					if (!value || value === '') return true;
					const numValue = this.parseShareCapitalValidationValue(value);
					return !isNaN(numValue) && numValue >= 0 && numValue <= 1000000000000000;
				},
				this.translateService.translate('company-details.shareCapital.range', 'client'),
				'shareCapital'
			),
			this.inputRules.custom(
				(value) => {
					if (!value || value === '') return true;
					const decimalPart = this.parseShareCapitalValidationValue(value).toString().split('.')[1];
					return !decimalPart || decimalPart.length <= 2;
				},
				this.translateService.translate('company-details.shareCapital.decimalPlaces', 'client'),
				'shareCapitalDecimal'
			),
		];
	}

	private parseShareCapitalValidationValue(value: string | number): number {
		if (typeof value === 'number') {
			return value;
		}

		return this.utilitiesService.TransformFromPipedValue(value);
	}

	@ViewChildren(CustomInputComponent) inputComponents!: QueryList<CustomInputComponent>;
	@ViewChildren(CustomSelectComponent) selectComponents!: QueryList<CustomSelectComponent>;

	// Field configurations for formManager
	private get fieldConfigs() {
		return [
			{ name: 'name', type: ComponentType.INPUT, validationRules: [this.inputRules.required()] },
			{ name: 'addressLine1', type: ComponentType.INPUT, validationRules: [this.inputRules.required()] },
			{ name: 'addressLine2', type: ComponentType.INPUT, validationRules: [] },
			{ name: 'addressLine3', type: ComponentType.INPUT, validationRules: [] },
			{ name: 'zipCode', type: ComponentType.INPUT, validationRules: [this.inputRules.required()] },
			{ name: 'city', type: ComponentType.INPUT, validationRules: [this.inputRules.required()] },
			{ name: 'activityCode', type: ComponentType.INPUT, validationRules: this.activityCodeValidationRules },
			{ name: 'timeZone', type: ComponentType.SELECT, validationRules: [this.selectRules.required()] },
			{ name: 'languageCode', type: ComponentType.SELECT, validationRules: [this.selectRules.required()] },
			{ name: 'shareCapital', type: ComponentType.INPUT, validationRules: this.shareCapitalValidationRules },
		];
	}

	constructor(
		private serverApiService: ServerApiService,
		private companyApiService: CompanyApiService,
		public appContext: AppContextService,
		private toasterService: ToasterService,
		private route: ActivatedRoute,
		public router: Router,
		private sanitizer: DomSanitizer,
		private logger: LogService,
		public utilitiesService: UtilitiesService,
		private translateService: TranslateService,
		private routeAuthorizationService: RouteAuthorizationService
	) {
		super();
	}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (!!this.loggedUser) {
				this.route.params.subscribe((params) => {
					const companyId = params['companyId'];
					if (
						companyId &&
						(!!this.loggedUser?.Companies.some((c) => c.Id === companyId) ||
							!!this.loggedUser?.IsSystemAdmin ||
							!!this.loggedUser?.IsSupportOperator)
					) {
						this.getCompanyDetails(companyId);
					} else {
						this.routeAuthorizationService.routeOnUnauthorized();
					}
				});
			}
		});
	}

	ngAfterViewInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		// Re-initialize with actual component references
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			this.fieldConfigs,
			this.translateService,
			this.inputComponents,
			this.selectComponents
		);
	}

	private initializeBreadcrumbs() {
		this.breadcrumbItems = [
			{
				label: this.translateService.translate('breadcrumb.home', 'client'),
				route: ['/private/home'],
			},
			{
				label: this.translateService.translate('breadcrumb.companies', 'client'),
				route: ['/private/companies'],
			},
			{
				label: this.company?.Name,
			},
		];
	}

	public get inThisCompany(): boolean {
		if (this.loggedUser?.Companies) {
			return !!this.loggedUser.Companies.find((c) => c.Id === this.company.Id);
		}
		return false;
	}

	public sanitize(url: string) {
		return this.sanitizer.bypassSecurityTrustUrl(url);
	}

	private getCompanyDetails(companyId: string) {
		this.isPerformingRequest = true;

		this.companyApiService
			.getCompany(companyId)
			.pipe(
				finalize(() => {
					this.initializeBreadcrumbs();
				})
			)
			.subscribe({
				next: (getCompanyOperationResult: OperationResult<Company>) => {
					if (getCompanyOperationResult.IsValid) {
						this.company = getCompanyOperationResult.Data;
						this.initializeFormValues();
					} else {
						this.processInvalidGetCompany(getCompanyOperationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.logger.error(httpErrorResponse.message);
					if (httpErrorResponse.status === 400) {
						this.routeAuthorizationService.routeOnUnauthorized();
					} else {
						this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
					}
				},
			});

		const referenceDataCalls = forkJoin({
			languageCodes: this.serverApiService.getLanguageCodes(),
			timeZones: this.serverApiService.getTimeZones(),
		});

		referenceDataCalls
			.pipe(
				finalize(() => {
					this.initializeFormValues();
					this.isPerformingRequest = false;
				})
			)
			.subscribe({
				next: (results) => {
					if (results.languageCodes.IsValid) {
						this.processValidLanguageCodesResult(results.languageCodes);
					} else {
						this.processinValidLanguageCodesResult(results.languageCodes);
					}

					if (results.timeZones.IsValid) {
						this.processValidTimeZonesResult(results.timeZones);
					} else {
						this.processInvalidTimeZonesResult(results.timeZones);
					}
				},
				error: (error) => {
					this.logger.error('[EditCompanyComponent::getReferenceData] Error loading reference data: ', error);
					this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_REFERENCE_DATA', 'list-errorCodes'));
					this.isPerformingRequest = false;
				},
			});
	}

	private processinValidLanguageCodesResult(operationResult: OperationResult<CodeDecodeValue[]>): any {
		this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_LANGUAGE_CODES', 'list-errorCodes'));
		operationResult.Errors?.forEach((element) => {
			this.logger.error('Loading language codes Error:' + element.Code, element.Field);
		});
	}

	private processInvalidTimeZonesResult(operationResult: OperationResult<TimeZoneValue[]>): any {
		this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_TIME_ZONESRESULTFALSE', 'list-errorCodes'));
		operationResult.Errors?.forEach((element) => {
			this.logger.error('Loading time zones Error:' + element.Code, element.Field);
		});
	}

	private processValidLanguageCodesResult(operationResult: OperationResult<CodeDecodeValue[]>): any {
		this.languageCodes = operationResult.Data;

		// Convert to SelectOption format
		this.languageOptions = this.languageCodes.map((lang) => ({
			label: `${lang.Code} - ${lang.Decode}`,
			value: lang.Code,
		}));
	}

	private processValidTimeZonesResult(operationResult: OperationResult<TimeZoneValue[]>): void {
		this.timeZones = this.utilitiesService.GetOrderedTimeZoneValues(operationResult.Data, false);

		// Convert to SelectOption format
		this.timeZoneOptions = this.timeZones.map((tz) => ({
			label: this.translateService.translate(tz.TimeZoneName, 'list-timeZones'),
			value: tz.IanaCode,
		}));
	}

	private processInvalidGetCompany(operationResult: OperationResult<Company>) {
		if (!!operationResult.Errors?.some((error) => error.Code === ServerErrors.Unauthorized)) {
			this.routeAuthorizationService.routeOnUnauthorized();
		} else {
			this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_COMPANYRESULTFALSE', 'list-errorCodes'));
			operationResult.Errors?.forEach((element) => {
				this.logger.error('Loading company Error:' + element.Code, element.Field);
			});
		}
	}

	private formValidation(): boolean {
		// Use formManager validation instead of manual validation
		if (this.formManager) {
			this.formManager.validateAll();
			this.isFormValid = this.formManager.isValid();
			return this.isFormValid;
		}

		// Fallback validation if formManager is not available
		if (!this.company) {
			const validationError = new OperationError();
			validationError.Code = 'COMPANY_REQUIRED';
			validationError.Field = 'Company';
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: [validationError],
				autoClose: false,
			});
			return false;
		}
		return true;
	}

	public updateCompanyData() {
		if (this.formValidation()) {
			const values = this.formManager!.getValues();
			this.company.Name = values.name;
			this.company.AddressLine1 = values.addressLine1;
			this.company.AddressLine2 = values.addressLine2;
			this.company.AddressLine3 = values.addressLine3;
			this.company.ZipCode = values.zipCode;
			this.company.City = values.city;
			this.company.ActivityCode = values.activityCode;
			this.company.TimeZone = values.timeZone;
			this.company.LanguageCode = values.languageCode;
			this.sendUpdateToserver(this.company);
		}
	}

	private sendUpdateToserver(company: Company): void {
		this.company.ActivityCodeList = 'CAE';
		const updateCompanyRequest = this.companyApiService.updateCompany(company);
		this.isPerformingRequest = true;
		updateCompanyRequest.subscribe({
			next: (opResult: OperationResult<boolean>) => {
				this.isPerformingRequest = false;
				if (opResult.IsValid) {
					let successMessage = this.translateService.translate('company-details.update.success', 'client');
					this.router.navigate(['/private/company/details', company.Id]);
					setTimeout(() => {
						this.toasterService.showSuccess(successMessage);
					}, 500);
				} else {
					let errors = opResult.Errors;

					if (errors && errors.length > 0) {
						this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
							errors: errors,
							autoClose: false,
						});
					}
					this.logger.error(
						'[EditCompanyComponent::updateCompanyRequest] Invalid result from updateCompany for company.Id: ',
						company.Id,
						opResult
					);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error(
					'[EditCompanyComponent::updateCompanyRequest] HttpError result from updateCompany for company.Id: ',
					company.Id,
					httpErrorResponse
				);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private initializeFormValues(): void {
		if (this.formManager && this.company) {
			this.formManager.setValues({
				name: this.company.Name || '',
				addressLine1: this.company.AddressLine1 || '',
				addressLine2: this.company.AddressLine2 || '',
				addressLine3: this.company.AddressLine3 || '',
				zipCode: this.company.ZipCode || '',
				city: this.company.City || '',
				activityCode: this.company.ActivityCode || '',
				timeZone: this.company.TimeZone || '',
				languageCode: this.company.LanguageCode || '',
				shareCapital: this.company.ShareCapital || '',
			});
			this.formManager.validateAll();
			this.isFormValid = this.formManager.isValid();
		}
	}

	public navigateToCompanyDetails(): void {
		this.router.navigate(['/private/company/details', this.company?.Id]);
	}

	// Unified numeric input handler
	onNumericKeyPress(
		event: KeyboardEvent,
		allowDecimals: boolean = true,
		maxDecimalPlaces: number = 2,
		maxValue: number = 1000000000000000
	): void {
		const key = event.key;

		if (event.ctrlKey || event.metaKey) {
			return;
		}

		if (
			key === 'Backspace' ||
			key === 'Delete' ||
			key === 'Tab' ||
			key === 'Escape' ||
			key === 'Enter' ||
			['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)
		) {
			return;
		}

		// Allow only numbers and optionally decimal point
		if (!/^[0-9.]$/.test(key)) {
			event.preventDefault();
			return;
		}

		// Handle decimal restrictions
		if (!allowDecimals && key === '.') {
			event.preventDefault();
			return;
		}

		const input = event.target as HTMLInputElement;
		const currentValue = input?.value ?? '';

		// Prevent multiple decimal points
		if (key === '.' && currentValue.includes('.')) {
			event.preventDefault();
			return;
		}

		// Enforce maximum decimal places - only check if we're adding after decimal point
		if (allowDecimals && key !== '.' && currentValue.includes('.')) {
			const selectionStart = input.selectionStart || 0;
			const decimalIndex = currentValue.indexOf('.');

			// Only enforce decimal limit if cursor is after the decimal point
			if (selectionStart > decimalIndex) {
				const decimalPart = currentValue.split('.')[1];
				if (decimalPart && decimalPart.length >= maxDecimalPlaces) {
					event.preventDefault();
					return;
				}
			}
		}

		// Enforce maximum value - check the potential result
		const selectionStart = input.selectionStart || 0;
		const selectionEnd = input.selectionEnd || 0;
		const beforeSelection = currentValue.substring(0, selectionStart);
		const afterSelection = currentValue.substring(selectionEnd);
		const potentialValue = beforeSelection + key + afterSelection;

		if (potentialValue) {
			const numValue = Number(potentialValue);
			if (numValue > maxValue) {
				event.preventDefault();
				return;
			}
		}
	}

	// Amount input handlers using proven patterns
	onShareCapitalChange(value: string): void {
		this.company.ShareCapital = this.utilitiesService.TransformFromPipedValue(value);
		if (this.formManager?.formFields?.['shareCapital']) {
			this.formManager.formFields['shareCapital'].value = this.company.ShareCapital;
		}
		this.onValidationChange('shareCapital', { isValid: true });
	}

	validateOnlyNumbers(event: KeyboardEvent): boolean {
		return this.utilitiesService.validateOnlyNumbers(event);
	}

	// Activity code still uses the integer-only handler
	onActivityCodeKeyPress(event: KeyboardEvent): void {
		this.onNumericKeyPress(event, false); // No decimals allowed
	}
}
